import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Eye, EyeOff, UserPlus, Shield, ArrowLeft, Info } from "lucide-react";
import api from "../lib/api";
import { getErrorMessage } from "../lib/utils";

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");  // MULTI-TENANT: Nome da empresa
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [checkingFirstUser, setCheckingFirstUser] = useState(true);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkFirstUser();
  }, []);

  const checkFirstUser = async () => {
    try {
      const response = await api.get("/auth/check-first-user");
      setIsFirstUser(response.data.is_first_user);
    } catch (error) {
      console.error("Error checking first user:", error);
    } finally {
      setCheckingFirstUser(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setLoading(true);

    try {
      await register(name, email, password, companyName || null);
      toast.success(`Empresa "${companyName || `Empresa de ${name}`}" criada com sucesso!`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao criar conta"));
    } finally {
      setLoading(false);
    }
  };

  if (checkingFirstUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex flex-1 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1629909615957-be38d48fbbe6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxhZXN0aGV0aWMlMjBjbGluaWMlMjBpbnRlcmlvciUyMGJyaWdodCUyMGNsZWFufGVufDB8fHx8MTc2NzgwODk2MHww&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white">
          <Shield className="h-16 w-16 mb-4" />
          <h2 className="font-heading text-4xl font-bold mb-4 text-center">
            {isFirstUser ? "Configuração Inicial" : "Criar Conta"}
          </h2>
          <p className="text-lg text-center text-white/90 max-w-md">
            {isFirstUser 
              ? "Você está criando a conta de Administrador do sistema. Após criar sua conta, você poderá adicionar colaboradores."
              : "Crie sua conta para acessar o sistema. Você poderá se tornar administrador nas configurações."
            }
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-primary mb-2">
              EstoqueClinic
            </h1>
            <p className="text-slate-500">
              Sistema de Gestão de Estoque para Clínicas de Estética
            </p>
          </div>

          <Card className="border-slate-200 shadow-soft border-primary/30">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl font-heading">Criar Conta</CardTitle>
                {isFirstUser ? (
                  <Badge className="bg-primary text-white">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    Colaborador
                  </Badge>
                )}
              </div>
              <CardDescription>
                {isFirstUser 
                  ? "Crie sua conta de Administrador para começar a usar o sistema."
                  : "Crie sua conta para acessar o sistema."
                }
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {/* MULTI-TENANT: Campo de nome da empresa */}
                <div className="space-y-2">
                  <Label htmlFor="companyName">Nome da Empresa/Clínica</Label>
                  <Input
                    id="companyName"
                    type="text"
                    placeholder="Ex: Clínica Estética Bella"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    data-testid="register-company-input"
                    className="h-11"
                  />
                  <p className="text-xs text-slate-500">Opcional - Se não informar, usaremos seu nome</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Seu Nome Completo</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Seu nome"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="register-name-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="register-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="register-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Mínimo de 6 caracteres</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="register-confirm-password-input"
                    className="h-11"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <>
                      <UserPlus className="mr-2 h-5 w-5" />
                      {isFirstUser ? "Criar Conta de Administrador" : "Criar Conta"}
                    </>
                  )}
                </Button>
                <Link
                  to="/login"
                  className="flex items-center justify-center text-sm text-slate-500 hover:text-primary"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Já tenho uma conta
                </Link>
              </CardFooter>
            </form>
          </Card>

          {!isFirstUser && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 text-sm">
                    Como se tornar Administrador?
                  </h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Após criar sua conta, acesse <strong>Configurações</strong> e clique em 
                    <strong> "Tornar-me Administrador"</strong> para ter acesso completo ao sistema.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
